import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost } from "../lib/api.ts";
import { OutlineButton } from "./FilterBar.tsx";
import { theme } from "../theme.ts";

interface ApiKey {
  id: number;
  name: string;
  role: "viewer" | "admin";
  created_by: string;
  created_at: number;
  last_used_at: number | null;
  revoked_at: number | null;
}
interface AuditEvent {
  at: number;
  event: string;
  user: string | null;
  detail: string | null;
}

const API_BASE = import.meta.env.VITE_API_BASE_URL || location.origin;
const fmt = (t: number | null) => (t ? new Date(t).toLocaleString() : "—");

/**
 * How AI agents get in. Claude (or any MCP client) connects to the MCP URL
 * and signs in with Microsoft; scripts and other services use an API key.
 * Recent sign-ins and tool calls are listed so access can be reviewed.
 */
export function AgentAccessCard() {
  const qc = useQueryClient();
  const keys = useQuery<{ keys: ApiKey[] }>({
    queryKey: ["api-keys"],
    queryFn: () => apiGet("/api/admin/api-keys"),
  });
  const audit = useQuery<{ events: AuditEvent[] }>({
    queryKey: ["audit"],
    queryFn: () => apiGet("/api/admin/audit?limit=30"),
  });
  const [name, setName] = useState("");
  const [role, setRole] = useState<"viewer" | "admin">("viewer");
  const [created, setCreated] = useState<{ name: string; key: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const create = async () => {
    setErr(null);
    try {
      const r = await apiPost<{ name: string; key: string }>("/api/admin/api-keys", { name, role });
      setCreated(r);
      setName("");
      qc.invalidateQueries({ queryKey: ["api-keys"] });
      qc.invalidateQueries({ queryKey: ["audit"] });
    } catch (e) {
      setErr((e as Error).message.replace(/^POST \S+ — \d+ /, ""));
    }
  };
  const revoke = async (k: ApiKey) => {
    if (!confirm(`Revoke "${k.name}"? Anything using it stops working immediately.`)) return;
    await apiDelete(`/api/admin/api-keys/${k.id}`);
    qc.invalidateQueries({ queryKey: ["api-keys"] });
    qc.invalidateQueries({ queryKey: ["audit"] });
  };

  const muted = { color: theme.color.textMuted, fontSize: theme.font.size.sm };
  const code = {
    fontFamily: theme.font.mono,
    background: theme.color.bg,
    padding: "2px 6px",
    borderRadius: theme.radius.sm,
    fontSize: theme.font.size.sm,
    wordBreak: "break-all" as const,
  };
  const th = { textAlign: "left" as const, padding: "6px 8px", ...muted, fontWeight: theme.font.weight.semibold };
  const td = { padding: "6px 8px", borderTop: `1px solid ${theme.color.border}`, fontSize: theme.font.size.sm };

  return (
    <div style={{ display: "grid", gap: theme.space.lg }}>
      <div style={{ display: "grid", gap: 6 }}>
        <div>
          <b>Claude and other AI agents</b> — add a custom connector with this URL. Each person signs in with their
          March Microsoft account and gets their own permissions:
        </div>
        <div>
          <span style={code}>{API_BASE}/mcp</span>
        </div>
        <div style={muted}>
          Read-only tools: what to order, forecast, stock health, SKU detail, forecast accuracy, alerts, data
          freshness, planning settings, and the invoices behind a number.
        </div>
      </div>

      <div style={{ display: "grid", gap: 8 }}>
        <div>
          <b>API keys</b> — for scripts and services that can't sign in. Send as{" "}
          <span style={code}>Authorization: Bearer mia_…</span> to <span style={code}>{API_BASE}/api/…</span> or the
          MCP URL.
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Key name, e.g. season-monitor"
            style={{ padding: "6px 10px", border: `1px solid ${theme.color.border}`, borderRadius: theme.radius.md, minWidth: 240 }}
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as "viewer" | "admin")}
            style={{ padding: "6px 8px", border: `1px solid ${theme.color.border}`, borderRadius: theme.radius.md }}
          >
            <option value="viewer">Read only</option>
            <option value="admin">Admin (can change settings)</option>
          </select>
          <OutlineButton onClick={create} disabled={name.trim().length < 2}>
            Create key
          </OutlineButton>
          {err && <span style={{ color: theme.color.danger, fontSize: theme.font.size.sm }}>{err}</span>}
        </div>
        {created && (
          <div style={{ background: theme.color.warningFaint, padding: 12, borderRadius: theme.radius.md, display: "grid", gap: 6 }}>
            <div>
              <b>Copy the key for "{created.name}" now</b> — it won't be shown again.
            </div>
            <span style={code}>{created.key}</span>
            <div>
              <OutlineButton onClick={() => void navigator.clipboard.writeText(created.key)}>Copy</OutlineButton>{" "}
              <OutlineButton onClick={() => setCreated(null)}>Done</OutlineButton>
            </div>
          </div>
        )}
        {keys.data && keys.data.keys.length > 0 && (
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr>
                <th style={th}>Name</th>
                <th style={th}>Access</th>
                <th style={th}>Created</th>
                <th style={th}>Last used</th>
                <th style={th} />
              </tr>
            </thead>
            <tbody>
              {keys.data.keys.map((k) => (
                <tr key={k.id} style={{ opacity: k.revoked_at ? 0.5 : 1 }}>
                  <td style={td}>{k.name}</td>
                  <td style={td}>{k.role === "admin" ? "Admin" : "Read only"}</td>
                  <td style={td}>
                    {fmt(k.created_at)} <span style={muted}>by {k.created_by}</span>
                  </td>
                  <td style={td}>{fmt(k.last_used_at)}</td>
                  <td style={{ ...td, textAlign: "right" }}>
                    {k.revoked_at ? (
                      <span style={muted}>revoked {fmt(k.revoked_at)}</span>
                    ) : (
                      <OutlineButton onClick={() => void revoke(k)}>Revoke</OutlineButton>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ display: "grid", gap: 6 }}>
        <b>Recent access</b>
        <div style={muted}>Sign-ins, refused sign-ins, key changes and agent tool calls (kept 90 days).</div>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <tbody>
            {(audit.data?.events ?? []).map((e, i) => (
              <tr key={i}>
                <td style={{ ...td, whiteSpace: "nowrap" }}>{fmt(e.at)}</td>
                <td style={td}>{e.user ?? "—"}</td>
                <td style={td}>{e.event.replace(/_/g, " ")}</td>
                <td style={{ ...td, ...muted, fontFamily: theme.font.mono }}>{e.detail ?? ""}</td>
              </tr>
            ))}
            {audit.data?.events.length === 0 && (
              <tr>
                <td style={{ ...td, ...muted }}>Nothing yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
